/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */
import { useHistory } from 'react-router-dom';
import { Button, Progress } from 'reactstrap';
import {
  getAnswered,
  getCompanyName,
  getCompletedDate,
  getEffectiveDate,
  getModulesDisplay,
  getPercentNa,
  getPercentNr,
  getPercentPrivate,
  getProgress,
  getTotalQuestions,
  getUnanswered,
} from './utils';
import { QUESTION, SURVEY } from '@constants/terminology';
import { BasicAlert } from '@g17eco/molecules/alert';
import { ColumnDef, Table } from '@g17eco/molecules/table';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { generateUrl } from '@routes/util';
import { ROUTES } from '@constants/routes';
import { naturalSort } from '@utils/index';
import { ExchangeSurveyWithCompany } from './types';

interface LatestSurveyTableProps {
  portfolioId: string;
  filteredSurveys: ExchangeSurveyWithCompany[];
  hasNoCompany: boolean;
  pageSize: number;
}

const columnStyles = {
  companyName: {
    style: {
      left: 0,
      width: 240,
      minWidth: 240,
    },
  },
  effectiveDate: {
    style: {
      left: 240,
      width: 160,
      minWidth: 160,
    },
  },
  completeDate: {
    style: {
      left: 400,
      width: 120,
      minWidth: 120,
    },
  },
};

export const LatestSurveyTable = ({ portfolioId, filteredSurveys, hasNoCompany, pageSize }: LatestSurveyTableProps) => {
  const history = useHistory();

  const handleClickCompany = (initiativeId: string) => {
    history.push(generateUrl(ROUTES.PORTFOLIO_TRACKER_COMPANY, { portfolioId: portfolioId, companyId: initiativeId }));
  };

  const tableColumns: ColumnDef<ExchangeSurveyWithCompany>[] = [
    {
      id: 'companyName',
      accessorKey: 'company.name',
      header: 'Company name',
      meta: {
        headerProps: columnStyles.companyName,
        cellProps: columnStyles.companyName,
        sticky: 'left',
      },
      cell: (c) => {
        const survey = c.row.original;
        return (
          <Button color='link' className='text-left' onClick={() => handleClickCompany(survey.company._id)}>
            {getCompanyName(survey)}
          </Button>
        );
      },
    },
    {
      id: 'effectiveDate',
      accessorKey: 'effectiveDate',
      header: `${SURVEY.CAPITALIZED_ADJECTIVE} date`,
      meta: {
        headerProps: columnStyles.effectiveDate,
        cellProps: columnStyles.effectiveDate,
        sticky: 'left',
      },
      cell: (c) => getEffectiveDate(c.row.original).display,
      sortingFn: (a, b) => naturalSort(getEffectiveDate(a.original).sort, getEffectiveDate(b.original).sort),
    },
    {
      id: 'completedDate',
      accessorKey: 'completedDate',
      header: 'Completed date',
      meta: {
        headerProps: columnStyles.completeDate,
        cellProps: columnStyles.completeDate,
        sticky: 'left',
      },
      cell: (c) => getCompletedDate(c.row.original),
    },
    {
      id: 'progress',
      accessorKey: 'progress',
      header: 'Progress',
      meta: {
        headerProps: {
          style: {
            minWidth: 110,
            maxWidth: 110,
          },
        },
        cellProps: {
          className: 'text-center',
          style: {
            minWidth: 110,
            maxWidth: 110,
          },
        },
      },
      cell: (c) => {
        const progress = getProgress(c.row.original);
        return (
          <SimpleTooltip text={progress.display}>
            <Progress value={progress.percent} />
          </SimpleTooltip>
        );
      },
      sortingFn: (a, b) => getProgress(a.original).percent - getProgress(b.original).percent,
    },
    {
      id: 'total',
      accessorKey: 'total',
      header: QUESTION.CAPITALIZED_PLURAL,
      meta: {
        cellProps: {
          className: 'text-center',
        },
      },
      cell: (c) => getTotalQuestions(c.row.original),
    },
    {
      id: 'unanswered',
      accessorKey: 'unanswered',
      header: 'Unanswered',
      meta: {
        cellProps: {
          className: 'text-center',
        },
      },
      cell: (c) => getUnanswered(c.row.original),
      sortingFn: (a, b) => getUnanswered(a.original) - getUnanswered(b.original),
    },
    {
      id: 'answered',
      accessorKey: 'answered',
      header: 'Answered',
      meta: {
        cellProps: {
          className: 'text-center',
        },
      },
      cell: (c) => getAnswered(c.row.original),
    },
    {
      id: 'na',
      accessorKey: 'na',
      header: '% N/A',
      meta: {
        cellProps: {
          className: 'text-center',
        },
      },
      cell: (c) => getPercentNa(c.row.original).display,
      sortingFn: (a, b) => getPercentNa(a.original).percent - getPercentNa(b.original).percent,
    },
    {
      id: 'nr',
      accessorKey: 'nr',
      header: '% N/R',
      meta: {
        cellProps: {
          className: 'text-center',
        },
      },
      cell: (c) => getPercentNr(c.row.original).display,
      sortingFn: (a, b) => getPercentNr(a.original).percent - getPercentNr(b.original).percent,
    },
    {
      id: 'private',
      accessorKey: 'private',
      header: '% Private',
      meta: {
        cellProps: {
          className: 'text-center',
        },
      },
      cell: (c) => getPercentPrivate(c.row.original).display,
      sortingFn: (a, b) => getPercentPrivate(a.original).percent - getPercentPrivate(b.original).percent,
    },
    {
      id: 'modules',
      accessorKey: 'scope',
      header: 'Modules',
      meta: {
        cellProps: {
          className: 'text-left',
          style: { minWidth: 200 },
        },
      },
      cell: (c) => getModulesDisplay(c.row.original),
    },
  ];

  return (
    <div className='latest-survey-table'>
      <div className='table-wrapper'>
        <Table responsive={true} columns={tableColumns} data={filteredSurveys} pageSize={pageSize} sticky />
        {filteredSurveys?.length === 0 ? (
          <BasicAlert type='secondary' className='mt-2'>
            {hasNoCompany ? 'You have no companies assigned to this portfolio' : 'No company match your criteria'}
          </BasicAlert>
        ) : null}
      </div>
    </div>
  );
};
